import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { SELECTORS } from '../../constants/selectors';
import { ROTATION_INTERVAL_MS, getNextSlideIndex } from './floating-banner.logic';

// 本来 var(--z-overlay) を参照したいが、Tailwind v4 の @theme token が現状 :root に
// CSS variable として出力されない（tree-shaking）ため固定値。値は --z-overlay: 30 と同一。
// main.css 側で :root への明示出力が入ったら var() 参照へ戻す。
const Z_ACTIVE_CLASS = 'tw:z-[31]';
const Z_INACTIVE_CLASS = 'tw:z-[30]';

/**
 * フローティング案内バナー（TOP 右下）
 *
 * @element kd-floating-banner
 * @description Liquid が生成した複数 slide DOM をフェードで巡回する。
 *              [data-slide] 要素ごと opacity で切り替える（スライド丸ごとフェード）。
 *              閉じるボタン押下でバナー全体を hidden に切り替える。永続化はしない（リロード/再来訪で再表示）。
 *              prefers-reduced-motion: reduce の環境では巡回を停止する。
 *              ホバー/フォーカス中は巡回を一時停止し、ユーザー操作中にフォーカスが奪われないようにする。
 *              FOUC 回避のため Liquid 側で `hidden` 属性をデフォルト付与している。
 *
 * @attr data-autoplay - "false" で自動切り替えを無効化（省略時は有効）
 * @attr data-interval - 切り替え間隔（ミリ秒）。省略時は ROTATION_INTERVAL_MS のデフォルト値
 */
@customElement('kd-floating-banner')
export class KdFloatingBanner extends LitElement {
  private slides: HTMLElement[] = [];

  private closeButtons: HTMLButtonElement[] = [];

  private linkElements: HTMLAnchorElement[] = [];

  private intervalId: number | null = null;

  private currentIndex = 0;

  private autoplay = true;

  private rotationInterval = ROTATION_INTERVAL_MS;

  // ホバーとフォーカスは独立して pause を要求できる。両方解除されたときのみ巡回再開。
  private isPointerInside = false;

  private isFocusInside = false;

  createRenderRoot() {
    return this;
  }

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.autoplay = this.dataset.autoplay !== 'false';
    this.rotationInterval = parseInt(this.dataset.interval ?? '') || ROTATION_INTERVAL_MS;

    this.slides = Array.from(this.querySelectorAll<HTMLElement>(SELECTORS.FLOATING_BANNER.SLIDE));
    this.closeButtons = Array.from(
      this.querySelectorAll<HTMLButtonElement>(SELECTORS.FLOATING_BANNER.CLOSE),
    );
    this.linkElements = Array.from(
      this.querySelectorAll<HTMLAnchorElement>(SELECTORS.FLOATING_BANNER.LINK),
    );

    if (this.slides.length === 0) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.trackEvent('floating_banner_impression', 0);

    this.closeButtons.forEach((btn) => btn.addEventListener('click', this.handleClose));
    this.linkElements.forEach((link) => link.addEventListener('click', this.handleLinkClick));

    if (this.autoplay && this.slides.length >= 2 && !this.prefersReducedMotion()) {
      this.startRotation();
      // ホバー/フォーカス中は巡回を一時停止する（自動切替でフォーカスが奪われるのを防ぐ）
      this.addEventListener('mouseenter', this.handlePointerEnter);
      this.addEventListener('mouseleave', this.handlePointerLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopRotation();
    this.closeButtons.forEach((btn) => btn.removeEventListener('click', this.handleClose));
    this.linkElements.forEach((link) => link.removeEventListener('click', this.handleLinkClick));
    this.removeEventListener('mouseenter', this.handlePointerEnter);
    this.removeEventListener('mouseleave', this.handlePointerLeave);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
  }

  private startRotation(): void {
    this.intervalId = window.setInterval(() => {
      this.currentIndex = getNextSlideIndex(this.currentIndex, this.slides.length);
      this.applyActiveSlide();
      this.trackEvent('floating_banner_impression', this.currentIndex);
    }, this.rotationInterval);
  }

  private stopRotation(): void {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private applyActiveSlide(): void {
    this.slides.forEach((slide, i) => {
      const isActive = i === this.currentIndex;

      // スライド丸ごと opacity で切替。z-index は active を前面に出すために併用する。
      slide.classList.toggle(Z_ACTIVE_CLASS, isActive);
      slide.classList.toggle(Z_INACTIVE_CLASS, !isActive);
      slide.classList.toggle('tw:pointer-events-none', !isActive);
      slide.classList.toggle('tw:opacity-100', isActive);
      slide.classList.toggle('tw:opacity-0', !isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      // 非アクティブスライド内の focusable 要素を Tab フォーカス順から外す（WCAG 2.4.3）。
      // aria-hidden / opacity-0 はキーボードフォーカスには影響しないため tabindex で明示的に制御する。
      const focusables = slide.querySelectorAll<HTMLElement>(SELECTORS.FLOATING_BANNER.FOCUSABLE);
      focusables.forEach((el) => {
        if (isActive) {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', '-1');
        }
      });
    });
  }

  private prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  private handleClose = (): void => {
    this.trackEvent('floating_banner_close', this.currentIndex);
    this.stopRotation();
    this.hidden = true;
  };

  private handleLinkClick = (event: MouseEvent): void => {
    const slide = (event.currentTarget as HTMLElement).closest<HTMLElement>('[data-slide]');
    const index = slide ? parseInt(slide.dataset.index ?? '0') : this.currentIndex;
    this.trackEvent('floating_banner_click', index);
  };

  private handlePointerEnter = (): void => {
    this.isPointerInside = true;
    this.stopRotation();
  };

  private handlePointerLeave = (): void => {
    this.isPointerInside = false;
    this.tryResumeRotation();
  };

  private handleFocusIn = (): void => {
    this.isFocusInside = true;
    this.stopRotation();
  };

  private handleFocusOut = (): void => {
    this.isFocusInside = false;
    this.tryResumeRotation();
  };

  private tryResumeRotation(): void {
    if (this.hidden) return;
    if (!this.autoplay || this.slides.length < 2 || this.prefersReducedMotion()) return;
    if (this.isPointerInside || this.isFocusInside) return;
    if (this.intervalId !== null) return;
    this.startRotation();
  }

  private trackEvent(eventName: string, bannerIndex: number): void {
    const dl = (window as Window & { dataLayer?: Record<string, unknown>[] }).dataLayer;
    dl?.push({ event: eventName, banner_index: bannerIndex });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-floating-banner': KdFloatingBanner;
  }
}
